import {
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { produce } from "immer";
import { useEffect, useMemo, useRef, useState } from "react";

import { GraphQLAPI, GraphQLResult, queries } from "@examspark/api";
import { useSerializedQueryParam } from "@examspark/common-app";
import {
  BucketAggregation,
  ParsedSearchableResourceConnectionNew,
  QuerySearchArgs,
  SearchableResourceConnectionNew,
  SearchableResourceFilterInput,
  SearchableResourceSortableFields,
  SearchableSortDirection,
} from "@examspark/types";

import { FilterData } from "./Browse";

export type SearchResultProducer = (
  item: ParsedSearchableResourceConnectionNew,
) => ParsedSearchableResourceConnectionNew;

export type SearchResultUpdater = (producer: SearchResultProducer) => void;

type UseSearchProps = {
  queryKey?: any[];
  searchLabel?: string;
  filter?: FilterData;
  enabled?: boolean;
  limit?: number;
  nextToken?: string;
  sort?: Array<{
    field: SearchableResourceSortableFields;
    direction: SearchableSortDirection;
  }>;
};

export const filterDataToSearchableResourceFilterInput = (params: {
  filter: FilterData;
  baseFilter: SearchableResourceFilterInput;
}): SearchableResourceFilterInput => {
  const _filter = params.baseFilter;

  if (params?.filter?.query?.length) {
    _filter.and?.push({
      name: {
        matchPhrase: params.filter.query,
      },
    });
  }

  if (params?.filter?.subject?.length) {
    _filter.and?.push({
      or: [params.filter.subject].map((subject) => ({
        subject: {
          eq: subject,
        },
      })),
    });
  }

  if (params?.filter?.grade?.length) {
    _filter.and?.push({
      or: params.filter.grade.map((subject) => ({
        grade: {
          eq: subject,
        },
      })),
    });
  }

  if (params?.filter?.bankId?.length) {
    _filter.and?.push({
      or: params.filter.bankId.map((bankId) => ({
        bankId: {
          eq: bankId,
        },
      })),
    });
  }

  if (params?.filter?.languages?.length) {
    _filter.and?.push({
      or: params.filter.languages.map((language) => ({
        language: {
          eq: language,
        },
      })),
    });
  }

  if (params?.filter?.objectiveScope != null) {
    _filter.and?.push({
      objectiveScope: { eq: params.filter.objectiveScope },
    });
  }

  if (params?.filter?.objectiveIds?.length) {
    _filter.and?.push({
      or: params.filter.objectiveIds.map((objectiveId) => ({
        objectiveId: { eq: objectiveId },
      })),
    });
  }

  if (params?.filter?.tags) {
    Object.entries(params.filter.tags).forEach(([key, values]) => {
      _filter.and?.push({
        or: values.map((value) => ({
          tags: {
            eq: `${key}#${value}`,
          },
        })),
      });
    });
  }

  if (params?.filter?.type?.length && Array.isArray(_filter.and)) {
    const typesFilter: SearchableResourceFilterInput[] = [];

    for (const type of params.filter.type ?? []) {
      switch (type) {
        case "UNIT":
          typesFilter.push({ sk: { eq: "unit#v0" } });
          break;
        case "ITEM":
          typesFilter.push({ sk: { eq: "item#v0" } });
          break;
        case "VOCABULARY":
          typesFilter.push({ sk: { eq: "vocabulary" } });
          break;
        case "COURSE":
          typesFilter.push({ sk: { eq: "course#v0" } });
          break;
        case "DISTRICT_ASSESSMENT":
          typesFilter.push({
            sk: { eq: "exam#v0" },
            // unit-check'-created assessments are unbanked
            bankId: { exists: true },
            // does this really go _here_?
            isDeleted: { ne: true },
          });
          break;
        case "EXAM":
          typesFilter.push({ sk: { eq: "exam#v0" } });
          break;
        case "COURSE_VERIFICATION":
          typesFilter.push({ sk: { eq: "course-verification#v0" } });
          break;
        case "COURSE_PLAN":
          typesFilter.push({ sk: { eq: "course-plan#v0" } });
          break;
        case "CLARIFICATION":
        case "CONCEPTUAL_ALIGNMENT":
        case "VERTICAL_ALIGNMENT":
          typesFilter.push({
            and: [{ sk: { eq: "objectives-view#v0" } }, { type: { eq: type } }],
          });
          break;
        case "PERFORMANCE_TASK":
        case "DOCUMENT":
        case "PASSAGE":
        case "RUBRIC":
          typesFilter.push({
            and: [{ sk: { eq: "resource#v0" } }, { type: { eq: type } }],
          });
          break;
      }
    }

    _filter.and?.push({
      or: typesFilter,
    });
  }

  return _filter;
};

/**
 * Powers bonfire-wide browse page. Should be used for complex search filters
 * and aggregations. */
export const useSearch = (params?: UseSearchProps) => {
  const client = useQueryClient();
  const limit = params?.limit ?? 50;
  const filter = useMemo(() => {
    const sks = [
      "course#v0",
      "unit#v0",
      "item#v0",
      "vocabulary",
      "exam#v0",
      "objectives-view#v0",
      "course-verification#v0",
      "course-plan#v0",
    ];
    const resourceTypes = ["RUBRIC", "PERFORMANCE_TASK"];

    const _filter: SearchableResourceFilterInput = {
      and: [
        {
          or: [
            ...sks.map((skValue) => ({ sk: { eq: skValue } })),

            {
              sk: {
                eq: "resource#v0",
              },
              or: resourceTypes.map((typeValue) => ({
                type: { eq: typeValue },
              })),
            },
          ],
        },
      ],
    };

    if (params?.filter) {
      return filterDataToSearchableResourceFilterInput({
        filter: params.filter,
        baseFilter: _filter,
      });
    }

    // console.log("filter", _filter);
    return _filter;
  }, [params?.filter]);

  const getQueryKey = () => [
    "browse",
    ...(params?.queryKey ?? []),
    filter,
    params?.nextToken,
  ];

  const { data, refetch, isFetching } = useQuery({
    queryKey: getQueryKey(),
    keepPreviousData: true,
    enabled: params?.enabled ?? true,
    queryFn: async () => {
      const variables: QuerySearchArgs = {
        filter,
        limit,
        nextToken: params?.nextToken,
        aggsNew: [
          {
            name: "subject",
            type: "terms",
            field: "subject",
          },
          {
            name: "grade",
            type: "terms",
            field: "grade",
            size: 20, // defaults to 10, but we want more to show all grades
          },
          {
            name: "bankId",
            type: "terms",
            field: "bankId",
          },
          {
            name: "objectiveScope",
            type: "terms",
            field: "objectiveScope",
            size: 50,
          },
          {
            name: "languages",
            type: "terms",
            field: "language",
          },
          {
            name: "objectiveId",
            type: "terms",
            field: "objectiveId",
            size: 50,
          },
          {
            name: "type",
            type: "terms",
            field: "type",
          },
          {
            name: "sk",
            type: "terms",
            field: "sk",
          },
          {
            name: "tagsCourse",
            type: "nested",
            path: "tags",
            aggs: [
              {
                name: "values",
                type: "filter",
                query: JSON.stringify({
                  terms: {
                    "tags.key.keyword": ["course-id"],
                  },
                }),
                aggs: [
                  {
                    name: "values",
                    type: "terms",
                    size: 15,
                    field: "tags.value",
                  },
                ],
              },
            ],
          },
          {
            name: "tagsUnit",
            type: "nested",
            path: "tags",
            aggs: [
              {
                name: "values",
                type: "filter",
                query: JSON.stringify({
                  terms: {
                    "tags.key.keyword": ["unit-id"],
                  },
                }),
                aggs: [
                  {
                    name: "values",
                    type: "terms",
                    size: 30,
                    field: "tags.value",
                  },
                ],
              },
            ],
          },
        ],
        sort: params?.sort ?? [
          {
            field: SearchableResourceSortableFields.UpdatedAt,
            direction: SearchableSortDirection.Desc,
          },
        ],
      };

      const result = (await GraphQLAPI.query<{
        search: SearchableResourceConnectionNew;
      }>({
        query: queries.search.search({
          label: params?.searchLabel,
          fields: `
            id
            sk
            idInc
            name
            description
            type
            bankId
            workspaceId
            createdAt
            updatedAt
            grade
            subject
            tags { key value }
          `,
        }),
        variables,
      })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

      const next = produce(result.data?.search, (draft) => {
        if (!draft) return;
        if (typeof draft.aggregations === "string") {
          draft.aggregations = JSON.parse(draft.aggregations);
        }
      }) as ParsedSearchableResourceConnectionNew;

      // console.log("next", next.aggregations);
      return next;
    },
  });

  const updateResults: SearchResultUpdater = (
    producer: SearchResultProducer,
  ) => {
    client.setQueriesData<ParsedSearchableResourceConnectionNew>(
      ["browse"],
      (prev) => {
        if (prev === undefined) return prev;
        return producer(prev);
      },
    );
  };

  return {
    data,
    refetch,
    isFetching,
    updateResults,
  };
};

/** @deprecated use method in `common/search.ts`  */
export const getTermAggregationBuckets = (
  aggs: Record<
    string,
    {
      buckets: BucketAggregation[];
    }
  >,
  key: string,
) => {
  return aggs[key]?.buckets ?? [];
};

/** @deprecated use method in `common/search.ts`  */
type Aggregations = Record<
  string,
  {
    buckets: BucketAggregation[];
  }
>;

/** @deprecated use method in `common/search.ts`  */
export const getTermAggregationBucketValue = (
  aggs: Aggregations,
  key: string,
  bucket: string,
) => {
  return aggs[key]?.buckets.find((b) => b.key === bucket)?.doc_count;
};

export type useSearchFn = (params: UseSearchProps) => Pick<
  UseQueryResult<ParsedSearchableResourceConnectionNew>,
  "data" | "refetch" | "isFetching"
> & {
  updateResults?: SearchResultUpdater;
};

export const usePaginatedSearch = (
  params: UseSearchProps,
  useSearchFn: useSearchFn = useSearch,
) => {
  const [nextTokens, setNextTokens] = useState<string[]>([""]);
  const searchResults = useSearchFn({
    ...params,
    nextToken: nextTokens[nextTokens.length - 1] || undefined,
  });

  const totalPages = Math.ceil(
    (searchResults.data?.total ?? 0) / (params.limit ?? 50),
  );
  const currentPage = nextTokens.length;

  // nextToken still appears on the last page of results, so
  const hasNextPage =
    searchResults.data?.nextToken !== null && currentPage < totalPages;
  const hasPreviousPage = nextTokens.length > 1;

  const fetchNextPage = () => {
    if (hasNextPage) {
      setNextTokens((nextTokens) => [
        ...nextTokens,
        searchResults.data?.nextToken ?? "",
      ]);
    }
  };

  const fetchPreviousPage = () => {
    if (hasPreviousPage) {
      setNextTokens((nextTokens) => nextTokens.slice(0, nextTokens.length - 1));
    }
  };

  const currentFilters = useRef(params.filter);
  useEffect(() => {
    if (!isEqual(params.filter, currentFilters.current)) {
      setNextTokens([""]);
      currentFilters.current = params.filter;
    }
  }, [params.filter]);

  return {
    ...searchResults,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    currentPage,
    totalPages,
  };
};
function isEqual(filter?: FilterData, current?: FilterData) {
  if (!filter && !current) return true;
  if (!filter || !current) return false;
  return JSON.stringify(filter) === JSON.stringify(current);
}

export const useFilterQueryParam = (defaultSearchFilter: FilterData) => {
  return useSerializedQueryParam("filter", defaultSearchFilter);
};
